// streams.js
//
// Live government streams from api2 (/streams/*): which governments are
// streaming on YouTube right now, which have a stream scheduled, and recent
// recordings, with embed code for each.
//
// Not routed through fetchApi2. That helper caches every answer for ten
// minutes, which is right for spending and wrong here: a stream's status is
// re-checked every 2 minutes upstream, and the guide asks for a refresh once a
// minute. So this reads fresh each time, with its own AbortSignal, since no
// other component shares the promise.

import { Capacitor } from '@capacitor/core';

import { API2_BASE, Api2Error } from './api2';

export const LIVE_REFRESH_MS = 60 * 1000;

// Everything for one place: city, county, state and federal, split into live,
// upcoming and recent.
//
// Called with a Census GEOID when the saved location has one -- a city's
// (7 digits; the API finds its county from it) or a county's (5) -- else with
// the state alone, else with nothing, which is the national federal bodies
// only. `address` is accepted because the API takes it, but the app does not
// send one: see usePlaceStreams.
// How many streams /streams/place returns per section (live, upcoming, recent).
// The endpoint does not page -- its `total` is the count it returned, and it
// takes no offset -- so this is the whole list a section can ever show. Set
// well above a busy day's recordings (roughly 170 channels feed a place);
// VideoFeed shows them a few at a time.
export const PLACE_STREAMS_LIMIT = 100;

export const fetchPlaceStreams = async ({ geoid, address, state } = {}, { signal } = {}) => {
  const url = new URL('/streams/place', API2_BASE);
  if (geoid) url.searchParams.set('geoid', geoid);
  else if (address) url.searchParams.set('address', address);
  else if (state) url.searchParams.set('state', state);
  url.searchParams.set('limit', String(PLACE_STREAMS_LIMIT));

  const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, signal });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body.detail === 'string' ? body.detail : null;
    throw new Api2Error(detail || `api2 ${response.status} for /streams/place`, response.status, detail);
  }
  return response.json();
};

// Local first: a city council meeting is rarer and nearer to the reader than
// the day's federal hearings.
export const SCOPE_ORDER = { city: 0, county: 1, state: 2, federal: 3 };

export const sortByScope = (rows) =>
  rows.slice().sort((a, b) => {
    const scope = (SCOPE_ORDER[a.scope] ?? 9) - (SCOPE_ORDER[b.scope] ?? 9);
    if (scope !== 0) return scope;
    return (b.viewers || 0) - (a.viewers || 0);
  });

export const resultsOf = (section) =>
  Array.isArray(section?.results) ? section.results.filter((row) => row && row.video_id) : [];

export const scopeLabel = (scope, t) =>
  ({
    city: t('common:liveScopeCity', 'City'),
    county: t('common:liveScopeCounty', 'County'),
    state: t('common:liveScopeState', 'State'),
    federal: t('common:liveScopeFederal', 'Federal'),
  })[scope] || null;

// The government, not the YouTube channel name: "Cerritos city" says whose
// meeting it is, "CityofCerritos" only says who uploaded it.
export const governmentName = (stream) =>
  stream?.channel?.linked_from?.name || stream?.channel?.title || null;

// The API's own thumbnail when it gives one; otherwise YouTube's standard still
// for the video, which exists for every public video.
export const thumbnailFor = (stream) => {
  if (typeof stream?.thumbnail === 'string' && stream.thumbnail) return stream.thumbnail;
  return stream?.video_id ? `https://i.ytimg.com/vi/${encodeURIComponent(stream.video_id)}/hqdefault.jpg` : null;
};

// The YouTube address. It stays good after the stream ends: YouTube keeps the
// recording there.
export const streamWatchUrl = (stream) =>
  stream?.watch_url || (stream?.video_id ? `https://www.youtube.com/watch?v=${stream.video_id}` : null);

// When it went live, else when it is scheduled, else when it ended. Raw, since
// the feed's date row and CollectionItem each format it themselves.
export const streamDate = (stream) =>
  stream?.actual_start || stream?.scheduled_start || stream?.actual_end || null;

// A stream as the collection stores it. The card (through FeedItem) and the
// player both save through this shape, keyed on the YouTube address, so a
// stream saved from one shows as saved in the other. feedName/branch/topic stay
// English, per the stored-data convention.
export const streamCollectionItem = (stream) => {
  const url = streamWatchUrl(stream);
  return {
    title: stream?.title || '',
    url,
    docLink: url,
    date: streamDate(stream),
    feedName: governmentName(stream) || 'Government stream',
    branch: 'Live Streams',
    topic: stream?.scope || 'N/A',
  };
};

// ─── Player ───────────────────────────────────────────────────────────────────
//
// YouTube refuses an embed that arrives without a referrer (Error 153, "Video
// player configuration error"). The iOS app's WKWebView runs on
// capacitor://localhost, which sends none, so on iOS the player is not framed
// directly: the app frames public/yt-embed.html on news.govglance.org, which is
// served over https and frames YouTube itself, so the request carries a real
// referrer. Web and Android already send one and keep the direct embed.
//
// The wrapper has to be deployed to Vercel to work; the copy cap sync puts in
// the app bundle is never loaded. It only frames YouTube /embed/ URLs.

const EMBED_WRAPPER = 'https://news.govglance.org/yt-embed.html';

// The privacy-enhanced player playing inline. playsinline keeps iOS from taking
// the video full screen the moment it starts.
const directPlayerUrl = (embedUrl) => {
  if (!embedUrl) return null;
  const joiner = embedUrl.includes('?') ? '&' : '?';
  return `${embedUrl}${joiner}autoplay=1&playsinline=1&rel=0`;
};

export const playerUrl = (embedUrl) => {
  const direct = directPlayerUrl(embedUrl);
  if (!direct) return null;
  return Capacitor.getPlatform() === 'ios'
    ? `${EMBED_WRAPPER}?src=${encodeURIComponent(direct)}`
    : direct;
};

export const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

// "live for 1 hr 12 min · 118 watching": how long a live stream has run and how
// many are watching, each left out when the API does not give it (YouTube hides
// the count when the owner asks).
export const liveStats = (stream, t) =>
  [
    typeof stream?.live_minutes === 'number'
      ? stream.live_minutes >= 60
        ? t('common:liveForHours', 'live for {{hours}} hr {{minutes}} min', {
            hours: Math.floor(stream.live_minutes / 60),
            minutes: stream.live_minutes % 60,
          })
        : t('common:liveForMinutes', 'live for {{count}} min', { count: stream.live_minutes })
      : null,
    typeof stream?.viewers === 'number'
      ? t('common:liveViewers', '{{count}} watching', { count: stream.viewers.toLocaleString() })
      : null,
  ]
    .filter(Boolean)
    .join(' \u00b7 ');

// Scheduled and end times arrive in UTC. Shown in the reader's own time: the
// time alone for today, with the weekday this week, with the date beyond.
export const formatStreamTime = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const now = new Date();
  const time = { hour: 'numeric', minute: '2-digit' };
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString(undefined, time);
  const days = Math.abs(date - now) / 86400000;
  if (days < 6) return date.toLocaleString(undefined, { weekday: 'short', ...time });
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', ...time });
};